import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ChevronDownIcon, SearchIcon, XIcon, UserIcon } from 'lucide-react';
import type { ClientRecord } from '../../hooks/useClients';

interface SearchableClientSelectProps {
  clients: ClientRecord[];
  value: string; // 'all' or client ID
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function SearchableClientSelect({
  clients,
  value,
  onChange,
  placeholder = 'Select a client',
  className = '',
}: SearchableClientSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Get selected client display text
  const selectedClient = useMemo(() => {
    if (value === 'all') return { name: 'All Clients', status: null };
    return clients.find(c => c.id === value);
  }, [value, clients]);

  // Filter and group clients
  const { filteredClients, groupedClients } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    // Filter clients
    const filtered = clients.filter(client =>
      client.name.toLowerCase().includes(query) ||
      client.phone.includes(query) ||
      (client.goal && client.goal.toLowerCase().includes(query))
    );

    // Group by status
    const grouped = {
      active: filtered.filter(c => c.status === 'active'),
      lead: filtered.filter(c => c.status === 'lead'),
      past: filtered.filter(c => c.status === 'past'),
      other: filtered.filter(c => !['active', 'lead', 'past'].includes(c.status || '')),
    };

    return { filteredClients: filtered, groupedClients: grouped };
  }, [clients, searchQuery]);

  // Flatten groups for keyboard navigation
  const flatOptions = useMemo(() => {
    const options: Array<{ type: 'all' | 'client'; id: string; client?: ClientRecord }> = [
      { type: 'all' as const, id: 'all' }
    ];

    const addGroup = (group: ClientRecord[]) => {
      group.forEach(client => {
        options.push({ type: 'client' as const, id: client.id, client });
      });
    };

    addGroup(groupedClients.active);
    addGroup(groupedClients.lead);
    addGroup(groupedClients.past);
    addGroup(groupedClients.other);

    return options;
  }, [groupedClients]);

  // Open dropdown and focus search input
  const openDropdown = useCallback(() => {
    setIsOpen(true);
    setSearchQuery('');
    setFocusedIndex(-1);
    // Focus search input on next tick
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, []);

  // Close dropdown
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setFocusedIndex(-1);
    buttonRef.current?.focus();
  }, []);

  // Handle selection
  const handleSelect = useCallback((clientId: string) => {
    onChange(clientId);
    closeDropdown();
  }, [onChange, closeDropdown]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        openDropdown();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => {
          const next = prev < flatOptions.length - 1 ? prev + 1 : prev;
          scrollToOption(next);
          return next;
        });
        break;

      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => {
          const next = prev > 0 ? prev - 1 : prev;
          scrollToOption(next);
          return next;
        });
        break;

      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < flatOptions.length) {
          handleSelect(flatOptions[focusedIndex].id);
        }
        break;

      case 'Escape':
        e.preventDefault();
        closeDropdown();
        break;

      case 'Tab':
        closeDropdown();
        break;
    }
  }, [isOpen, focusedIndex, flatOptions, handleSelect, openDropdown, closeDropdown]);

  // Scroll focused option into view
  const scrollToOption = (index: number) => {
    if (!listRef.current) return;
    const option = listRef.current.querySelector(`[data-option-index="${index}"]`);
    if (option) {
      option.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  // Click outside handler
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeDropdown]);

  // Status badge component
  const StatusBadge = ({ status }: { status?: string | null }) => {
    if (!status) return null;

    const badges = {
      active: 'bg-green-100 text-green-700',
      lead: 'bg-blue-100 text-blue-700',
      past: 'bg-gray-100 text-gray-700',
      cold: 'bg-orange-100 text-orange-700',
    };

    const style = badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`px-2 py-0.5 text-xs rounded-full ${style}`}>
        {status}
      </span>
    );
  };

  // Client option component
  const ClientOption = ({
    client,
    isSelected,
    isFocused,
    optionIndex
  }: {
    client?: ClientRecord;
    isSelected: boolean;
    isFocused: boolean;
    optionIndex: number;
  }) => {
    const isAllClients = !client;
    const displayName = isAllClients ? 'All Clients' : client.name;

    return (
      <button
        type="button"
        data-option-index={optionIndex}
        onClick={() => handleSelect(isAllClients ? 'all' : client.id)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
          isFocused
            ? 'bg-blue-50'
            : isSelected
              ? 'bg-blue-100'
              : 'hover:bg-gray-50'
        }`}
        role="option"
        aria-selected={isSelected}
      >
        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
          <UserIcon className="w-4 h-4 text-gray-500" />
        </div>

        {/* Client Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm truncate ${isSelected ? 'font-semibold text-[#7B9EAE]' : 'text-gray-900'}`}>
              {displayName}
            </p>
            {!isAllClients && <StatusBadge status={client.status} />}
          </div>
          {!isAllClients && (
            <p className="text-xs text-gray-500 truncate">{client.phone}</p>
          )}
        </div>

        {/* Selected Indicator */}
        {isSelected && (
          <div className="w-2 h-2 rounded-full bg-[#7B9EAE] flex-shrink-0" />
        )}
      </button>
    );
  };

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => isOpen ? closeDropdown() : openDropdown()}
        onKeyDown={handleKeyDown}
        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl bg-white text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-[#7B9EAE] focus:border-transparent transition-all"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select client filter"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <UserIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-900 truncate">
            {selectedClient?.name || placeholder}
          </span>
          {selectedClient?.status && <StatusBadge status={selectedClient.status} />}
        </div>
        <ChevronDownIcon
          className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
          role="listbox"
          aria-label="Client options"
        >
          {/* Search Input */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-3">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setFocusedIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                placeholder="Search by name, phone, or goal..."
                className="w-full pl-10 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7B9EAE] focus:border-transparent"
                aria-label="Search clients"
                aria-autocomplete="list"
                aria-controls="client-list"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    searchInputRef.current?.focus();
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Client List */}
          <div
            ref={listRef}
            id="client-list"
            className="max-h-80 overflow-y-auto"
          >
            {/* All Clients Option */}
            {!searchQuery && (
              <ClientOption
                isSelected={value === 'all'}
                isFocused={focusedIndex === 0}
                optionIndex={0}
              />
            )}

            {/* Active Clients */}
            {groupedClients.active.length > 0 && (
              <div>
                <div className="px-3 py-2 bg-gray-50 border-y border-gray-100">
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Active ({groupedClients.active.length})
                  </h3>
                </div>
                {groupedClients.active.map((client, idx) => {
                  const optionIndex = flatOptions.findIndex(o => o.id === client.id);
                  return (
                    <ClientOption
                      key={client.id}
                      client={client}
                      isSelected={value === client.id}
                      isFocused={focusedIndex === optionIndex}
                      optionIndex={optionIndex}
                    />
                  );
                })}
              </div>
            )}

            {/* Lead Clients */}
            {groupedClients.lead.length > 0 && (
              <div>
                <div className="px-3 py-2 bg-gray-50 border-y border-gray-100">
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Leads ({groupedClients.lead.length})
                  </h3>
                </div>
                {groupedClients.lead.map((client) => {
                  const optionIndex = flatOptions.findIndex(o => o.id === client.id);
                  return (
                    <ClientOption
                      key={client.id}
                      client={client}
                      isSelected={value === client.id}
                      isFocused={focusedIndex === optionIndex}
                      optionIndex={optionIndex}
                    />
                  );
                })}
              </div>
            )}

            {/* Past Clients */}
            {groupedClients.past.length > 0 && (
              <div>
                <div className="px-3 py-2 bg-gray-50 border-y border-gray-100">
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Past ({groupedClients.past.length})
                  </h3>
                </div>
                {groupedClients.past.map((client) => {
                  const optionIndex = flatOptions.findIndex(o => o.id === client.id);
                  return (
                    <ClientOption
                      key={client.id}
                      client={client}
                      isSelected={value === client.id}
                      isFocused={focusedIndex === optionIndex}
                      optionIndex={optionIndex}
                    />
                  );
                })}
              </div>
            )}

            {/* Other Clients */}
            {groupedClients.other.length > 0 && (
              <div>
                <div className="px-3 py-2 bg-gray-50 border-y border-gray-100">
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Other ({groupedClients.other.length})
                  </h3>
                </div>
                {groupedClients.other.map((client) => {
                  const optionIndex = flatOptions.findIndex(o => o.id === client.id);
                  return (
                    <ClientOption
                      key={client.id}
                      client={client}
                      isSelected={value === client.id}
                      isFocused={focusedIndex === optionIndex}
                      optionIndex={optionIndex}
                    />
                  );
                })}
              </div>
            )}

            {/* Empty State */}
            {filteredClients.length === 0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-gray-500">
                  {searchQuery ? 'No clients found matching your search' : 'No clients available'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
